import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Star, MapPin, ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Favorites() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchFavorites();
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await apiClient.get('/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (artistId) => {
    try {
      await apiClient.delete(`/favorites/${artistId}`);
      toast.success('Removed from favorites');
      fetchFavorites();
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="hover:bg-white/10"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold gradient-text flex items-center gap-2">
              <Heart className="text-red-500 fill-red-500" size={32} />
              My Favorites
            </h1>
            <p className="text-white/60">{favorites.length} saved artists</p>
          </div>
        </div>

        {favorites.length === 0 ? (
          <Card className="glass-default border-white/10">
            <CardContent className="p-12 text-center">
              <Heart size={48} className="mx-auto mb-4 text-white/40" />
              <p className="text-xl text-white/60 mb-4">No favorites yet</p>
              <p className="text-white/40 mb-6">Start browsing artists and save your favorites!</p>
              <Button onClick={() => navigate('/browse')} className="bg-violet-600 hover:bg-violet-500">
                Browse Artists
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((fav) => (
              <Card
                key={fav.favorite_id}
                className="glass-default border-white/10 hover-lift overflow-hidden group"
              >
                <div 
                  className="aspect-video relative overflow-hidden bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 cursor-pointer"
                  onClick={() => navigate(`/artist/${fav.artist.artist_id}`)}
                >
                  {fav.artist.media_urls?.[0] ? (
                    <img
                      src={fav.artist.media_urls[0]}
                      alt="Artist"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Star className="w-12 h-12 text-violet-400/50" />
                    </div>
                  )}
                  {fav.artist.rating > 0 && (
                    <div className="absolute top-2 left-2 glass-heavy rounded-full px-2 py-1 flex items-center gap-1">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-xs font-semibold">{(fav.artist.rating || 0).toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">
                        {fav.artist.bio?.split(' ').slice(0, 3).join(' ')}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <MapPin size={14} />
                        <span>{fav.artist.is_local ? 'Local' : 'Touring'}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(fav.artist.artist_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {fav.artist.genres?.slice(0, 2).map((genre, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">
                        {genre}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-end">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/artist/${fav.artist.artist_id}`)}
                      className="bg-violet-600 hover:bg-violet-500"
                    >
                      Book Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
